import React, { useEffect, useMemo, useState } from 'react';
import {
    Box,
    Typography,
    Button,
    IconButton,
    Card,
    Stack,
    Chip,
    Pagination,
    Select,
    MenuItem,
    FormControl,
} from '@mui/material';
import {
    ArrowBack as ArrowBackIcon,
    Add as AddIcon,
    Delete as DeleteIcon,
    Edit as EditIcon,
    Visibility as ViewIcon,
    ContentCopy as CopyIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { initializeDB } from '../../config/db';
import { CSEP_STORE, JHA_STORE } from '../../config/constants';
import { generateUUID } from '../../utils/uuid';

const STATUS_CHIPS = {
    new: { label: 'New', color: 'info' },
    draft: { label: 'Draft', color: 'default' },
    in_progress: { label: 'In Progress', color: 'warning' },
    completed: { label: 'Completed', color: 'success' },
    submitted: { label: 'Submitted', color: 'success' }
};

export default function CsepList() {
    const navigate = useNavigate();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);

    useEffect(() => { load(); }, []);

    async function load() {
        setLoading(true);
        try {
            const db = await initializeDB();
            if (db.objectStoreNames.contains(CSEP_STORE)) {
                const tx = db.transaction(CSEP_STORE, 'readonly');
                const store = tx.objectStore(CSEP_STORE);
                const allForms = await store.getAll();
                // Sort by updatedAt descending (newest first)
                const sorted = (allForms || [])
                    .sort((a, b) => {
                        const dateA = new Date(a.updatedAt || a.createdAt || 0);
                        const dateB = new Date(b.updatedAt || b.createdAt || 0);
                        return dateB - dateA;
                    });
                setItems(sorted);
            }
        } catch (e) {
            console.error('Error loading forms:', e);
        } finally {
            setLoading(false);
        }
    }

    // Pagination calculations
    const paginatedItems = useMemo(() => {
        const startIndex = (currentPage - 1) * itemsPerPage;
        const endIndex = startIndex + itemsPerPage;
        return items.slice(startIndex, endIndex);
    }, [items, currentPage, itemsPerPage]);

    const totalPages = Math.ceil(items.length / itemsPerPage);

    const handlePageChange = (event, value) => {
        setCurrentPage(value);
    };

    const handleItemsPerPageChange = (event) => {
        setItemsPerPage(event.target.value);
        setCurrentPage(1);
    };

    const handleDelete = async (uuid) => {
        if (!window.confirm('Are you sure you want to delete this permit?')) return;
        try {
            const db = await initializeDB();

            // Get the CSEP to check if it's linked to a JHA
            const csepRecord = await db.get(CSEP_STORE, uuid);

            // If linked to JHA, unlink it first
            if (csepRecord?.linkedJhaId && db.objectStoreNames.contains(JHA_STORE)) {
                const jha = await db.get(JHA_STORE, csepRecord.linkedJhaId);
                if (jha) {
                    const updatedDocsPermits = (jha.stepTwo?.docsPermits || []).map(doc => {
                        if (doc.linkedCsepUuid === uuid) {
                            return { ...doc, linkedCsepUuid: null, linkedCsepData: null };
                        }
                        return doc;
                    });
                    const updatedCustomDocs = (jha.stepTwo?.customDocsPermits || []).map(doc => {
                        if (doc.linkedCsepUuid === uuid) {
                            return { ...doc, linkedCsepUuid: null, linkedCsepData: null };
                        }
                        return doc;
                    });

                    await db.put(JHA_STORE, {
                        ...jha,
                        stepTwo: {
                            ...jha.stepTwo,
                            docsPermits: updatedDocsPermits,
                            customDocsPermits: updatedCustomDocs
                        }
                    });
                }
            }

            // Delete the CSEP
            await db.delete(CSEP_STORE, uuid);
            await load();
        } catch (e) {
            console.error('Error deleting form:', e);
        }
    };

    const handleEdit = (rec) => {
        const lastStep = rec.meta?.lastStep || 1;
        const path = lastStep === 2 ? '/csep/step-2' : '/csep/step-1';
        navigate(path, { state: { csep: rec, isNew: false } });
    };

    const handleView = (rec) => {
        navigate('/csep/step-1', { state: { csep: rec, isNew: false, viewMode: true } });
    };

    const handleCopyRecord = async (record, e) => {
        e.stopPropagation();

        // Allow copying only for submitted/completed records
        if (record.status !== 'submitted' && record.status !== 'completed') {
            return;
        }

        const newUuid = generateUUID();
        const now = new Date().toISOString();

        const newRecord = {
            ...record,
            uuid: newUuid,
            id: null,
            localId: newUuid,
            backendId: null,
            syncStatus: null,
            status: 'draft',
            createdAt: now,
            updatedAt: now,
            submittedAt: null,
            submittedBy: null,
            submittedByName: null,
            stepOne: {
                ...record.stepOne,
                // Reset date/time to now
                dateTime: now,
            },
            stepTwo: {
                ...record.stepTwo,
                // Reset entry date/time
                entryDateTime: null,
                // Clear verifications (keep structure but reset signatures/dates)
                verifications: Object.keys(record.stepTwo?.verifications || {}).reduce((acc, key) => {
                    acc[key] = { date: null, signedBy: null, signature: null };
                    return acc;
                }, {}),
                // Clear air monitoring readings
                airMonitoringReadings: {},
                // Clear other contaminants readings
                otherContaminants: (record.stepTwo?.otherContaminants || []).map(c => ({
                    ...c,
                    check1: { value: '', time: null },
                    check2: { value: '', time: null },
                    check3: { value: '', time: null }
                })),
                // Clear authorization signature
                entrySupervisorSignature: null,
                entrySupervisorSignatureDate: null,
            },
            meta: { lastStep: 1 },
            // Clear email status
            emailSent: false,
            emailSentAt: null,
            emailSentTo: null
        };

        try {
            const db = await initializeDB();
            const tx = db.transaction(CSEP_STORE, 'readwrite');
            const store = tx.objectStore(CSEP_STORE);
            await store.put(newRecord);
            await tx.done;

            // Refresh list and navigate to edit
            await load();
            navigate('/csep/step-1', { state: { csep: newRecord, isNew: false } });
        } catch (e) {
            console.error('Error copying record:', e);
        }
    };

    const cardTitle = (rec) => {
        const location = rec.stepOne?.location || '';
        const jobCode = rec.stepOne?.jobCode || '';
        const jobName = rec.stepOne?.jobName || '';
        if (location) return location;
        if (jobCode || jobName) return `${jobCode}${jobCode && jobName ? ' - ' : ''}${jobName}`;
        return 'New Permit';
    };

    const formatDate = (dateString) => {
        if (!dateString) return '—';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
    };

    const getStatusChip = (status) => {
        const config = STATUS_CHIPS[status] || STATUS_CHIPS.new;
        return (
            <Chip
                label={config.label}
                size="small"
                color={config.color}
                sx={{ height: '20px', fontSize: '0.7rem' }}
            />
        );
    };

    return (
        <Box sx={{
            height: 'calc(100vh - 60px)',
            backgroundColor: '#f5f5f5',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
        }}>
            {/* Header */}
            <Box sx={{ backgroundColor: 'white', borderBottom: '1px solid #e0e0e0', p: 1.5, flexShrink: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <IconButton onClick={() => navigate('/main')} edge="start">
                            <ArrowBackIcon />
                        </IconButton>
                        <Typography variant="h6" fontWeight={600}>Confined Space Entry Permits</Typography>
                    </Box>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => navigate('/csep/step-1', { state: { isNew: true } })}
                        sx={{ backgroundColor: '#4caf50', '&:hover': { backgroundColor: '#45a049' } }}
                    >
                        New Permit
                    </Button>
                </Box>
            </Box>

            {/* List */}
            <Box sx={{ p: 1.5, flexGrow: 1, overflowY: 'auto' }}>
                {loading ? (
                    <Card sx={{ p: 4 }}>
                        <Typography>Loading...</Typography>
                    </Card>
                ) : items.length === 0 ? (
                    <Card sx={{ p: 4, textAlign: 'center' }}>
                        <Typography variant="h6" color="text.secondary">No permits found</Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            Click "New Permit" to create one
                        </Typography>
                    </Card>
                ) : (
                    <>
                        <Stack spacing={1}>
                            {paginatedItems.map((rec) => {
                                const isSubmitted = rec.status === 'submitted' || rec.status === 'completed';
                                return (
                                    <Card key={rec.uuid} sx={{ p: 1.5 }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <Box sx={{ flex: 1 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                                    <Typography variant="subtitle1" sx={{ fontSize: '0.95rem', fontWeight: 500 }}>
                                                        {cardTitle(rec)}
                                                    </Typography>
                                                    {getStatusChip(rec.status)}
                                                </Box>
                                                <Box sx={{ display: 'flex', gap: 2, mt: 0.3 }}>
                                                    {rec.stepOne?.date && (
                                                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
                                                            Date: {formatDate(rec.stepOne.date)}
                                                        </Typography>
                                                    )}
                                                    {rec.stepOne?.entrySupervisorName && (
                                                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
                                                            Supervisor: {rec.stepOne.entrySupervisorName}
                                                        </Typography>
                                                    )}
                                                </Box>
                                                {rec.updatedAt && (
                                                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                                                        Updated: {new Date(rec.updatedAt).toLocaleString()}
                                                    </Typography>
                                                )}
                                            </Box>
                                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                {isSubmitted ? (
                                                    <>
                                                        <IconButton
                                                            size="small"
                                                            color="info"
                                                            onClick={() => handleView(rec)}
                                                            title="View"
                                                        >
                                                            <ViewIcon fontSize="small" />
                                                        </IconButton>
                                                        <IconButton
                                                            size="small"
                                                            sx={{ color: '#666' }}
                                                            onClick={(e) => handleCopyRecord(rec, e)}
                                                            title="Copy"
                                                        >
                                                            <CopyIcon fontSize="small" />
                                                        </IconButton>
                                                    </>
                                                ) : (
                                                    <>
                                                        <IconButton
                                                            size="small"
                                                            color="primary"
                                                            onClick={() => handleEdit(rec)}
                                                            title="Edit"
                                                        >
                                                            <EditIcon fontSize="small" />
                                                        </IconButton>
                                                        <IconButton
                                                            size="small"
                                                            color="error"
                                                            onClick={() => handleDelete(rec.uuid)}
                                                            title="Delete"
                                                        >
                                                            <DeleteIcon fontSize="small" />
                                                        </IconButton>
                                                    </>
                                                )}
                                            </Box>
                                        </Box>
                                    </Card>
                                );
                            })}
                        </Stack>

                        {/* Pagination controls */}
                        {totalPages > 1 && (
                            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <FormControl size="small" sx={{ minWidth: 120 }}>
                                    <Select value={itemsPerPage} onChange={handleItemsPerPageChange}>
                                        <MenuItem value={10}>10 per page</MenuItem>
                                        <MenuItem value={25}>25 per page</MenuItem>
                                        <MenuItem value={50}>50 per page</MenuItem>
                                    </Select>
                                </FormControl>
                                <Pagination
                                    count={totalPages}
                                    page={currentPage}
                                    onChange={handlePageChange}
                                    color="primary"
                                    size="small"
                                />
                            </Box>
                        )}
                    </>
                )}
            </Box>
        </Box>
    );
}
